import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Box, Button } from '@mui/material'
import { CoursePurchaseState } from '../../../../../features/types'
import { BoldText, H3 } from '../../../../../layouts/TextStyles'
import FireIcon from '../../../../../assets/icons/Fire.svg'
import PlayCircle from '../../../../../assets/icons/PlayCircle.svg'
import NotePencil from '../../../../../assets/icons/NotePencil.svg'
import ChartLineUp from '../../../../../assets/icons/ChartLineUp.svg'
import HourglassLow from '../../../../../assets/icons/HourglassLow.svg'
import PresentationChart from '../../../../../assets/icons/PresentationChart.svg'
import ChartBar from '../../../../../assets/icons/ChartBar.svg'
import {
    calculateNumberFromPercentage,
    capitalize,
} from '../../../../../utils/functions'
import CourseDetailsDialog from './CourseDetailsDialog'
import CourseTag from './CourseTag'
import {
    buttonBox,
    card,
    discount,
    discountSloganBox,
    flexCenter,
    flexWrap,
    moreBtn,
    priceBox,
    sloganBox,
} from '../style'

interface Props {
    coursePurchase: CoursePurchaseState
}

const PurchaseCourseCard: React.FC<Props> = ({ coursePurchase }) => {
    const styles = {
        discountPrice: {
            color: '#75798A',
            fontSize: '13px',
            textDecoration: coursePurchase.discount > 0 && 'line-through',
            mr: '4px',
            fontWeight: 500,
        },
    }
    const navigate = useNavigate()
    const { formatMessage, locale } = useIntl()
    const [openDialog, setOpenDialog] = useState(false)

    const discountPrice = calculateNumberFromPercentage(
        coursePurchase.price,
        coursePurchase.discount,
    )

    const getCourseDuration = (): string => {
        if (coursePurchase.duration > 31) {
            const month = Math.trunc(coursePurchase.duration / 30)
            return month + ' ' + formatMessage({ id: 'month' }).toLowerCase()
        }

        return coursePurchase.duration + ' ' + formatMessage({ id: 'day' })
    }

    const tags = [
        {
            id: 1,
            text: formatMessage({ id: 'videoLessons' }),
            icon: PlayCircle,
        },
        { id: 2, text: formatMessage({ id: 'topicTests' }), icon: NotePencil },
        {
            id: 3,
            text: formatMessage({ id: 'workOnMistake' }).toLowerCase(),
            icon: ChartLineUp,
        },
        { id: 4, text: getCourseDuration(), icon: HourglassLow },
        {
            id: 5,
            text: formatMessage({ id: 'videoSolutionsOfQuestion' }),
            icon: PresentationChart,
        },
        {
            id: 6,
            text: formatMessage({ id: 'progressAnalysis' }),
            icon: ChartBar,
        },
    ]

    return (
        <Box
            component="div"
            sx={card}
        >
            <Box sx={flexWrap}>
                {tags.map((tag) => (
                    <CourseTag
                        key={tag.id}
                        text={tag.text}
                        icon={tag.icon}
                    />
                ))}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
                <H3 theme={{ weight: 500 }}>
                    {coursePurchase[`title${capitalize(locale)}`]}
                </H3>

                <Box sx={sloganBox}>
                    {coursePurchase[`slogan${capitalize(locale)}`]}
                </Box>
            </Box>

            <Box sx={priceBox}>
                <Box sx={discountSloganBox}>
                    <img
                        src={FireIcon}
                        alt="fire"
                    />
                    {coursePurchase[`discountSlogan${capitalize(locale)}`]}
                </Box>

                <Box sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={flexCenter}>
                        <Box sx={styles.discountPrice}>
                            {coursePurchase.price}т
                        </Box>

                        {coursePurchase.discount > 0 && (
                            <Box sx={discount}>-{coursePurchase.discount}%</Box>
                        )}
                    </Box>

                    {coursePurchase.discount > 0 && (
                        <BoldText color="#0C8F65">{discountPrice} т</BoldText>
                    )}
                </Box>
            </Box>

            <Box sx={buttonBox}>
                <Button
                    variant="outlined"
                    fullWidth
                    sx={moreBtn}
                    onClick={() => setOpenDialog(true)}
                >
                    {formatMessage({ id: 'moreInfo' })}
                </Button>

                <Button
                    variant="contained"
                    onClick={() =>
                        navigate(
                            `checkout/${coursePurchase.courseId}/${coursePurchase.id}`,
                        )
                    }
                    fullWidth
                >
                    {formatMessage({ id: 'enrollToCourse' })}
                </Button>
            </Box>

            <CourseDetailsDialog
                open={openDialog}
                coursePurchase={coursePurchase}
                endTime={coursePurchase.endTime}
                discountPrice={discountPrice}
                getCourseDuration={getCourseDuration}
                handleClose={() => setOpenDialog(false)}
            />
        </Box>
    )
}

export default PurchaseCourseCard
